import mime from 'mime'

const _FALLBACK_FILE_NAME = 'download'

export const promptFileDownload = async (fileContent: Blob, fileName?: string) => {
  try {
    const url = window.URL.createObjectURL(fileContent)
    const a = document.createElement('a')

    a.href = url
    a.download = fileName ?? _FALLBACK_FILE_NAME
    document.body.appendChild(a)
    a.click()

    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  } catch (e) {
    console.error(e)
  }
}

const SUPPORTED_MEDIA_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'audio/mp4',
  'audio/webm',
  'audio/flac',
  'audio/ogg',
  'audio/mpeg',
  'audio/x-m4a',
  'audio/aac',
  'audio/wav',
  'audio/x-wav',
])

export const isMediaFile = (file: File) => {
  if (isMediaFileType(file.type)) {
    return true
  }
  const type = mime.getType(file.name)
  return type ? isMediaFileType(type) : false
}

export const isMediaFileType = (type: File['type']) => SUPPORTED_MEDIA_TYPES.has(type)

const METADATA_STRIPPABLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png'])

const STRIPPED_JPEG_QUALITY = 0.92

// Redraws the image on a canvas so the uploaded file holds only pixels, dropping EXIF (GPS location, device) and other metadata.
// Other files, including GIFs (which would lose their animation), are returned unchanged.
export const stripImageMetadata = async (file: File): Promise<File> => {
  if (!METADATA_STRIPPABLE_IMAGE_TYPES.has(file.type)) {
    return file
  }
  const bitmap = await createImageBitmap(file)
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close()
    throw new Error('Could not get canvas context')
  }
  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('Could not encode image'))), file.type, STRIPPED_JPEG_QUALITY)
  })
  return new File([blob], file.name, { type: file.type, lastModified: file.lastModified })
}
